// Screen 8, Data (SPEC §8 Screen 8; J-067). The year comes from the shell's selector and defaults to
// the newest configured one; Calculate runs for it. SPEC §15.3 gives the screen to the roles that load
// files (the admin only since CR-004, D-1220), so any other role reads why the page is empty; the server refuses them either way.
// A bundle's load page (J-185) renders in this page's <Outlet/> in place of the screen.
import { createFileRoute, Outlet, useChildMatches } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { AlertBanner } from "@vbltc/ui/components/alert-banner/alert-banner";

import { metadataQueryOptions } from "@/features/aco-shell/api/metadata";
import { ShellStatusMessage } from "@/features/aco-shell/components/shell-status-message";
import { isProduction, resolveYear } from "@/features/aco-shell/default-aco";
import { shellStatus } from "@/features/aco-shell/shell-status";
import { usePermission } from "@/features/auth/use-permission";
import { DataScreen } from "@/features/aco-data/components/data-screen";

export const Route = createFileRoute("/_authenticated/data")({
  staticData: { screenName: "Data" },
  component: DataPage,
});

function DataPage() {
  const search = Route.useSearch();
  const canLoad = usePermission({ acoData: ["load"] });
  const canCalculate = usePermission({ acoData: ["calculate"] });
  const canReset = usePermission({ acoDemo: ["reset"] });
  const metadata = useQuery({ ...metadataQueryOptions(), enabled: canLoad });
  const child = useChildMatches().length > 0;
  if (!canLoad)
    return (
      <AlertBanner variant="info" title="This screen is for the roles that load data">
        Uploading files, calculating and resetting the demo need the admin role.
      </AlertBanner>
    );
  if (child) return <Outlet />;
  // J-209 (inbox 06 PR D item 7): a failed metadata read says so instead of "Loading.".
  const status = shellStatus(metadata);
  if (status !== "ready")
    return <ShellStatusMessage status={status} onRetry={() => void metadata.refetch()} />;
  const year = resolveYear(metadata.data, search.year, search.aco);
  if (year === undefined)
    return <p className="text-muted-foreground text-sm">No performance year is configured.</p>;
  return (
    <DataScreen
      year={year}
      canCalculate={canCalculate}
      canReset={canReset}
      production={isProduction(metadata.data)}
    />
  );
}
